<script setup>

import { slugify } from '~~/shared/utilities/slugify';

const props = defineProps({
    showCounts: {
        type: Boolean,
        default: () => false
    },
    year: {
        type: Number,
        default: null
    }
})

const expanded = ref(new Set([]))
const tags = defineModel('tags')
const { data, error, pending } = await useAsyncData(`all-tags-${props.year || 'default'}`, () => $fetch('/api/v1/session-tags', { params: props.year ? { year: props.year } : {} }));

const check = (tag) => {
    const new_set = new Set(tags.value)
    if (new_set.has(tag)) {
        new_set.delete(tag)
    } else {
        new_set.add(tag)
    }
    tags.value = Array.from(new_set)
}

const toggle = (tag) => {
    if (expanded.value.has(tag)) {
        expanded.value.delete(tag)
    } else {
        expanded.value.add(tag)
    }
}

const hasSelectedChildren = (tag_name) => {
    if (data.value && data.value[tag_name]) {
        return data.value[tag_name].filter(tag => tags.value.indexOf(tag.id) !== -1).length
    }
    return 0
}

</script>
<template>
    <div class='session-tags'>
        <div class="tag-group" v-for="(tag_tags, tag_name) in data" :class="{ 'open': expanded.has(tag_name) }">
            <div class="tag-header" @click.prevent="toggle(tag_name)" >
                <h3>
                    <button class="tag" type='button' :id="`session-tag-${slugify(tag_name)}`" :aria-controls="`session-tag-group-${slugify(tag_name)}`">{{ tag_name }}</button>
                    <span class="count" v-if="hasSelectedChildren(tag_name)">{{ hasSelectedChildren(tag_name) }}</span>
                </h3>
                <MessSvg type="AccordianPlus" v-if="!expanded.has(tag_name)" />
                <MessSvg type="AccordianMinus" v-else />
            </div>
            <div class='tag-wrapper'>
                <ul class="tags" role="region" :id="`session-tag-group-${slugify(tag_name)}`" :aria-labelledby="`session-tag-group-${slugify(tag_name)}`">
                    <li v-for="tag in tag_tags" :key="tag.tag">
                        <label class="tag" :aria-checked="tags.indexOf(tag.tag) !== -1" :for="`tag-${tag.slug}`" tabindex="0" @keyup.space="check(tag.id)" @click="check(tag.id)">
                            <input type="checkbox" tabindex="-1" :id="`tag-${tag.id}`" name="tags" :value="tag.id" v-model="tags" :aria-label="`Filter posts with the tag ${tag.name}`" />
                            <span class="tag-name">{{ tag.name }} <span class="count" v-if="showCounts">{{ tag.count }}</span></span>
                        </label>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    .session-tags {
        
        .tag-group {
        
            border-bottom: 1px solid var(--gray-light);
            padding: 1.25rem 0 1.125rem 0;

            &:first-child {
                padding-top: 0;
            }

            .tag-wrapper {
                display: grid;
                grid-template-rows: 0fr;
                transition: grid-template-rows 250ms ease-in-out;
                width: 100%;
                visibility: hidden;
                overflow: hidden;

                .tags {
                    overflow: hidden;
                }
            }

            &.open .tag-wrapper {
                padding-top: 1rem;
                grid-template-rows: 1fr;
                visibility: visible;
            }
        }

        .tag-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            cursor: pointer;

            @media (hover:hover) {
                &:hover {
                    h3 {
                        color: var(--magenta);
                    }
                    svg path {
                        fill: var(--magenta);
                    }
                }
            }
        }

        h3 {
            font-family: var(--font-hubspot-serif);
            font-size: calc(20 / var(--base-fs) * 1rem);
            font-weight: 400;
            letter-spacing: 0;
            margin: 0;
            line-height: 1.625rem;
            display: flex;
            align-items: end;

            span {
                margin-left: 0.85rem;
                color: var(--dark-pink);
                font-size: calc(16 / var(--base-fs) * 1rem);
                letter-spacing: -0.02rem;
            }
        }
    
        .tags li {
            width: 100%;
            margin-bottom: 0.75rem;
            padding: 0;
            font-size: calc(14 / var(--base-fs) * 1rem);
            font-weight: 300;
            line-height: 1.25rem;
            letter-spacing: 0.0175rem;
        }

        .tags li label {
            user-select: none;
            display: flex;
            align-items: center;
            gap: 0.75rem;
            cursor: pointer;
            font-size: calc(16 / var(--base-fs) * 1rem);

            @media(min-width: $tablet) {
                font-size: calc(14 / var(--base-fs) * 1rem);
            }

            &:hover {
                text-decoration: underline;
                text-underline-offset: 4px;
            }

            .tag-name {
                width: 100%;
                display: flex;
                .count {
                    margin-left: auto;
                    opacity: 0.25;
                }
            }

            input[type="checkbox"] {
                border: 1px solid var(--teal);
                box-sizing: border-box;
                width: 12px;
                height: 12px;
                padding: 0;
                border: 1px solid var(--teal-light);
                appearance: none;
                background-color: transparent;
                cursor: pointer;
                outline: none;
                transition: outline 0.1s;

                &:checked {
                    background-size: cover;
                    padding: 2px;
                }

                &:focus {
                    outline: 1px solid var(--orange-light);
                }

                &:not(:disabled):checked {
                    border-color: var(--orange-dark);
                    background-image: url('~/assets/img/check.svg');
                }
            }
        }
    }
</style>